/**
 * Editor for org template schedules with exclusivity and module dependency configuration.
 *
 * Renders an editable list of schedules. Each schedule has ID, name, and optional
 * multi-select fields for exclusiveWith (other schedules) and requiresModules.
 * ID fields carry an InfoHint explaining the docx tag rules, a live copyable
 * tag preview, and inline validation errors passed down from the configurator.
 * Uses brand-compliant colours (brand-* instead of blue) and typography.
 */

import { Plus } from 'lucide-react';
import { Button, TogglePill } from '../common';
import InfoHint from '../common/InfoHint';
import TagPreview from './TagPreview';
import { generateId } from '../../utils/generateId';
import { scheduleEnabledTag } from '../../utils/templateTags';
import NumberedItemCard from './NumberedItemCard';
import type { OrgSchedule, OrgModule } from '../../types/orgTemplate';

const SCHEDULE_ID_HINT =
  "The ID becomes the conditional tag pair that wraps this schedule's section in the Word " +
  'document. Start with a letter and use only letters, numbers, hyphens and underscores — ' +
  'no spaces. Hyphens become underscores in the tag.';

interface ScheduleEditorProps {
  schedules: OrgSchedule[];
  modules: OrgModule[];
  onChange: (schedules: OrgSchedule[]) => void;
  /** Schedule index → error message for that schedule's ID field. */
  errors?: Record<number, string>;
}

export default function ScheduleEditor({ schedules, modules, onChange, errors }: ScheduleEditorProps) {
  const handleAdd = () => {
    onChange([...schedules, { id: generateId('sched'), name: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(schedules.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: 'id' | 'name', value: string) => {
    const updated = schedules.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    onChange(updated);
  };

  const handleToggleExclusive = (index: number, scheduleId: string) => {
    const schedule = schedules[index];
    const current = schedule.exclusiveWith || [];
    const next = current.includes(scheduleId)
      ? current.filter((id) => id !== scheduleId)
      : [...current, scheduleId];
    const updated = schedules.map((s, i) =>
      i === index ? { ...s, exclusiveWith: next.length > 0 ? next : undefined } : s
    );
    onChange(updated);
  };

  const handleToggleModule = (index: number, moduleId: string) => {
    const schedule = schedules[index];
    const current = schedule.requiresModules || [];
    const next = current.includes(moduleId)
      ? current.filter((id) => id !== moduleId)
      : [...current, moduleId];
    const updated = schedules.map((s, i) =>
      i === index ? { ...s, requiresModules: next.length > 0 ? next : undefined } : s
    );
    onChange(updated);
  };

  return (
    <div className="space-y-5">
      {schedules.map((sched, sIdx) => (
        <NumberedItemCard
          key={sIdx}
          index={sIdx}
          title={sched.name || 'New Schedule'}
          identifier={sched.id || undefined}
          onRemove={() => handleRemove(sIdx)}
          removeAriaLabel={`Remove schedule ${sched.name || sched.id}`}
        >
          <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-1 text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide">
                  Schedule ID
                  <InfoHint text={SCHEDULE_ID_HINT} label="Schedule ID format" />
                </label>
                <input
                  type="text"
                  value={sched.id}
                  onChange={(e) => handleChange(sIdx, 'id', e.target.value)}
                  aria-invalid={errors?.[sIdx] ? true : undefined}
                  className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-body-sm"
                  placeholder="e.g. data-centre"
                />
                {errors?.[sIdx] && (
                  <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                    {errors[sIdx]}
                  </p>
                )}
                <div className="mt-1">
                  <TagPreview tag={sched.id ? scheduleEnabledTag(sched.id) : ''} label="Schedule tag" />
                </div>
              </div>
              <div>
                <label className="block text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide">
                  Schedule Name
                </label>
                <input
                  type="text"
                  value={sched.name}
                  onChange={(e) => handleChange(sIdx, 'name', e.target.value)}
                  className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-body-sm"
                  placeholder="e.g. Data Centre Services"
                />
              </div>
            </div>

            {/* Exclusive With */}
            {schedules.length > 1 && (
              <div>
                <span className="block text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide mb-1">
                  Exclusive With
                </span>
                <div className="flex flex-wrap gap-2">
                  {schedules
                    .filter((_, i) => i !== sIdx)
                    .map((other) => {
                      const selected = (sched.exclusiveWith || []).includes(other.id);
                      return (
                        <TogglePill
                          key={other.id}
                          type="button"
                          selected={selected}
                          variant="amber"
                          onClick={() => handleToggleExclusive(sIdx, other.id)}
                        >
                          {other.name || other.id}
                        </TogglePill>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Requires Modules */}
            {modules.length > 0 && (
              <div>
                <span className="block text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide mb-1">
                  Requires Modules
                </span>
                <div className="flex flex-wrap gap-2">
                  {modules.map((mod) => {
                    const selected = (sched.requiresModules || []).includes(mod.id);
                    return (
                      <TogglePill
                        key={mod.id}
                        type="button"
                        selected={selected}
                        variant="brand"
                        onClick={() => handleToggleModule(sIdx, mod.id)}
                      >
                        {mod.name || mod.id}
                      </TogglePill>
                    );
                  })}
                </div>
              </div>
            )}
        </NumberedItemCard>
      ))}

      <Button variant="secondary" size="sm" onClick={handleAdd}>
        <Plus className="h-4 w-4 mr-1" />
        Add Schedule
      </Button>
    </div>
  );
}
